import { useCallback, useEffect, useRef, useState } from 'react';
import { AdviseActionNeededItem, AdviseActionNeededReport } from '../../../types/advise';
import {
  clearAdviseActionNeeded,
  fetchAdviseActionNeeded,
  snoozeAdviseActionNeeded
} from '../../../Api/Advise/ActionNeeded';
import { isNotFoundRefusal, refusalMessage } from '../../../utils/refusalMessage';
import { useShowRefusal } from './useShowRefusal';

const SNOOZE_FAILED = "Couldn't snooze the entry. Please try again.";
const CLEAR_FAILED = "Couldn't clear the entry. Please try again.";

type SaveError = Parameters<typeof refusalMessage>[0];

// Someone else already changed this entry, or the engagement left the dashboard.
const isStaleEntry = (error: SaveError) => isNotFoundRefusal(error) || error?.errorResponse?.response?.status === 422;

const withoutId = (ids: ReadonlySet<string>, id: string) => new Set([...ids].filter((pending) => pending !== id));

/**
 * The Advise Dashboard's Action Needed entries; a failed read leaves none, so the box stays hidden. Each answer is
 * the whole report as of its own request, so reads and saves are sent one at a time and the last answer is the newest.
 */
export const useEngagementActionNeeded = () => {
  const [report, setReport] = useState<AdviseActionNeededReport | null>(null);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const mounted = useRef(true);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const showRefusal = useShowRefusal();

  const inTurn = useCallback((request: () => Promise<void>) => {
    queue.current = queue.current.then(request);
    return queue.current;
  }, []);

  const read = useCallback(async () => {
    const { data } = await fetchAdviseActionNeeded();
    if (mounted.current) setReport(data);
  }, []);

  const refresh = useCallback(() => inTurn(read), [inTurn, read]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  const send = useCallback(
    (item: AdviseActionNeededItem, call: typeof snoozeAdviseActionNeeded, failed: string) => {
      setPendingIds((ids) => new Set(ids).add(item.engagement_id));
      return inTurn(async () => {
        const { data, error } = await call(item.engagement_id, item.condition);
        if (!mounted.current) return;

        if (data) {
          setReport(data);
        } else {
          showRefusal(refusalMessage(error, failed));
          if (isStaleEntry(error)) await read();
        }
        setPendingIds((ids) => withoutId(ids, item.engagement_id));
      });
    },
    [inTurn, read, showRefusal]
  );

  const snooze = useCallback(
    (item: AdviseActionNeededItem) => send(item, snoozeAdviseActionNeeded, SNOOZE_FAILED),
    [send]
  );
  const clear = useCallback(
    (item: AdviseActionNeededItem) => send(item, clearAdviseActionNeeded, CLEAR_FAILED),
    [send]
  );

  return { report, pendingIds, snooze, clear, refresh };
};
